import { useEffect, useRef, useState } from 'react';

interface QA { q: string; a: string; }

interface AIAnswerBlockProps {
  answers: QA[];
  title?: string;
  subtitle?: string;
}

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

export default function AIAnswerBlock({
  answers,
  title = 'Frequently Asked',
  subtitle = 'Direct answers to the most common questions about OTT Car Care and what we deliver.',
}: AIAnswerBlockProps) {
  const { ref, visible } = useReveal();

  return (
    <section
      ref={ref}
      className="py-24 px-6 lg:px-12 xl:px-16"
      style={{ background: 'linear-gradient(135deg, #0e0e0e 0%, #111008 100%)' }}
    >
      <div className="max-w-6xl mx-auto">
        <div className={`flex items-center gap-3 mb-4 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <span className="w-10 h-px bg-gold" />
          <span className="text-gold text-[10px] font-sans tracking-[0.35em] uppercase font-semibold">AI-Optimized Answers</span>
        </div>
        <h2 className={`font-serif text-white text-4xl lg:text-5xl font-bold mb-4 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {title}
        </h2>
        <p className={`text-white/40 text-base font-sans mb-16 max-w-2xl transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {subtitle}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/5">
          {answers.map((item, i) => (
            <div
              key={i}
              className={`bg-[#0d0d0d] p-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="w-8 h-8 flex items-center justify-center border border-gold/30 mb-6">
                <i className="ri-question-line text-gold text-sm" />
              </div>
              <h3 className="font-serif text-white text-lg font-semibold mb-4 leading-snug">
                {item.q}
              </h3>
              <p className="text-white/45 text-sm font-sans leading-relaxed">
                {item.a}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
